import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";
import { HouseLine, MagicWand, Ruler, MapPinLine, Sparkle } from "@phosphor-icons/react";

const FORMS = [
  { value: "uad_3_6", label: "UAD 3.6 (full)" },
  { value: "standard_1004", label: "Standard 1004" },
  { value: "desktop", label: "Desktop appraisal" },
  { value: "drive_by", label: "Drive-by (exterior only)" },
  { value: "review", label: "Review appraisal" },
];

export default function PostJob() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    job_type: "appraisal",
    address: "", city: "", state: "", zip_code: "",
    lot_size_sqft: "", house_sqft: "", form_type: "uad_3_6",
    travel_distance_mi: "", notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const runPreview = useCallback(async () => {
    if (!form.address || !form.city || !form.state) return;
    setPreviewing(true);
    try {
      const { data } = await api.post("/fee/preview", {
        ...form,
        job_type: form.form_type === "review" ? "review" : form.job_type,
        lot_size_sqft: Number(form.lot_size_sqft) || 0,
        house_sqft: Number(form.house_sqft) || 0,
        travel_distance_mi: Number(form.travel_distance_mi) || 0,
      });
      setPreview(data);
    } catch (e) {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  }, [form]);

  const submit = async () => {
    if (!form.address || !form.city || !form.state || !form.zip_code) {
      toast.error("Please complete the property address");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", {
        ...form,
        job_type: form.form_type === "review" ? "review" : form.job_type,
        lot_size_sqft: Number(form.lot_size_sqft) || 0,
        house_sqft: Number(form.house_sqft) || 0,
        travel_distance_mi: Number(form.travel_distance_mi) || 0,
      });
      toast.success("Job posted. Appraisers will start bidding.");
      navigate(`/jobs/${data.job_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post job");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-5xl grid lg:grid-cols-[1fr,360px] gap-8 lg:gap-12">
        <div>
          <div className="kicker">New order</div>
          <h1 className="display-serif text-4xl sm:text-5xl mt-2">Post an appraisal job.</h1>
          <p className="text-zinc-600 mt-3">Enter the property details. AI proposes a minimum fee so appraisers bid fairly.</p>

          <div className="mt-8 space-y-6">
            <section className="surface-card p-6 lg:p-8">
              <div className="flex items-center gap-2"><MapPinLine size={18} className="text-[#115E59]" weight="duotone" /><h3 className="display-serif text-xl">Property</h3></div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label>Street address</Label>
                  <Input value={form.address} onChange={setField("address")} data-testid="pj-address" placeholder="1234 Elm Street" />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={setField("city")} data-testid="pj-city" />
                </div>
                <div className="grid grid-cols-[1fr,1fr] gap-3">
                  <div>
                    <Label>State</Label>
                    <Input value={form.state} onChange={setField("state")} data-testid="pj-state" placeholder="CA" maxLength={2} />
                  </div>
                  <div>
                    <Label>ZIP</Label>
                    <Input value={form.zip_code} onChange={setField("zip_code")} data-testid="pj-zip" placeholder="90210" />
                  </div>
                </div>
              </div>
            </section>

            <section className="surface-card p-6 lg:p-8">
              <div className="flex items-center gap-2"><Ruler size={18} className="text-[#115E59]" weight="duotone" /><h3 className="display-serif text-xl">Size &amp; form</h3></div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>House sqft</Label>
                  <Input type="number" value={form.house_sqft} onChange={setField("house_sqft")} data-testid="pj-house-sqft" />
                </div>
                <div>
                  <Label>Lot size (sqft)</Label>
                  <Input type="number" value={form.lot_size_sqft} onChange={setField("lot_size_sqft")} data-testid="pj-lot-sqft" />
                </div>
                <div>
                  <Label>Travel distance (mi)</Label>
                  <Input type="number" value={form.travel_distance_mi} onChange={setField("travel_distance_mi")} data-testid="pj-travel" />
                </div>
                <div>
                  <Label>Form type</Label>
                  <select
                    value={form.form_type}
                    onChange={setField("form_type")}
                    data-testid="pj-form-type"
                    className="mt-1 w-full h-10 rounded-md border border-zinc-300 bg-white px-3 text-sm"
                  >
                    {FORMS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
              </div>
            </section>

            <section className="surface-card p-6 lg:p-8">
              <h3 className="display-serif text-xl">Notes for appraisers</h3>
              <Textarea value={form.notes} onChange={setField("notes")} data-testid="pj-notes" rows={4} placeholder="Anything unusual, e.g., recent renovation, gated community, tight deadline…" />
            </section>

            <div className="flex flex-wrap gap-3">
              <button onClick={runPreview} disabled={previewing} className="btn-outline-ink" data-testid="pj-preview-btn">
                <Sparkle size={16} weight="fill" /> {previewing ? "Thinking…" : "Preview AI fee"}
              </button>
              <button onClick={submit} disabled={submitting} className="btn-ink" data-testid="pj-submit-btn">
                {submitting ? "Posting…" : "Post job"}
              </button>
            </div>
          </div>
        </div>

        {/* sticky-ish preview panel */}
        <aside>
          <div className="lg:sticky lg:top-24 surface-card p-6 lg:p-7">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#FFEDD5] text-[#9A3412]">
                <MagicWand size={18} weight="duotone" />
              </span>
              <div className="kicker">AI minimum fee</div>
            </div>
            <div className="mt-4 display-serif text-5xl">
              {preview ? `$${preview.minimum_fee.toFixed(0)}` : "—"}
            </div>
            <p className="text-zinc-600 text-sm mt-2 min-h-[3rem]">
              {preview ? preview.rationale : "Fill in size and form, then click Preview AI fee."}
            </p>
            <div className="mt-6 kicker">What happens next</div>
            <ol className="mt-2 text-sm text-zinc-700 space-y-1 list-decimal pl-5">
              <li>Post your job (free).</li>
              <li>Appraisers bid: minimum + complexity add-ons.</li>
              <li>You pick a winner &amp; pay via PayPal escrow.</li>
              <li>Another appraiser peer-reviews the report.</li>
              <li>On approval, funds release automatically.</li>
            </ol>
          </div>
        </aside>
      </div>
    </PageLayout>
  );
}
